/**
 * WbTile — selecteerbare tegel met checkbox-semantiek.
 *
 * Vervangt chips: minimaal 48 px hoog, 2 px rand, geselecteerd = gele vulling
 * (wb-ophalen) + vinkje. Meerdere tegels mogen tegelijk aan staan.
 *
 * variant 'keuze' (standaard): geselecteerd = geel, het is een keuze.
 * variant 'klaar': geselecteerd = groen, het is een voltooiing (checklist
 * Bus laden / Routes klaarleggen; "ingeladen" is afleveren-semantiek).
 */

import { type ButtonHTMLAttributes } from 'react';
import clsx from 'clsx';

export interface WbTileProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  selected: boolean;
  onToggle: () => void;
  /** Tegel over de volle breedte van het grid. */
  wide?: boolean;
  variant?: 'keuze' | 'klaar';
  /** Eén-keuze-grid (radio-semantiek) in plaats van meerkeuze. */
  rol?: 'checkbox' | 'radio';
}

export function WbTile({ selected, onToggle, wide = false, variant = 'keuze', rol = 'checkbox', className, children, ...props }: WbTileProps) {
  return (
    <button
      type="button"
      role={rol}
      aria-checked={selected}
      onClick={onToggle}
      className={clsx(
        'wb-focus min-h-[48px] w-full flex items-center gap-2 px-3 py-2 rounded-wb border-wb text-left text-base font-semibold leading-tight select-none',
        selected
          ? (variant === 'klaar' ? 'bg-wb-afleveren border-wb-rand text-wb-inkt' : 'bg-wb-ophalen border-wb-rand text-wb-inkt')
          : 'bg-wb-vlak border-wb-gedempt text-wb-inkt active:bg-wb-gedempt',
        wide && 'col-span-full',
        className
      )}
      {...props}
    >
      <span
        aria-hidden
        className={clsx(
          'w-6 h-6 shrink-0 border-wb flex items-center justify-center',
          rol === 'radio' ? 'rounded-full' : 'rounded-[4px]',
          selected ? 'bg-wb-inkt border-wb-inkt text-white' : 'border-wb-inkt3 bg-white text-transparent'
        )}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </span>
      <span className="flex-1 min-w-0">{children}</span>
    </button>
  );
}
